import type { AuctionListItems, BidListItem } from '@b-stock/auction-api-client'
import {
  GetAccountUniqueBidsAuctionStatusEnum,
  GetAccountUniqueBidsBidStatusEnum,
} from '@b-stock/auction-api-client'
import {
  fetchAllResultsById,
  getAuctionsClient,
  getBidsClient,
  makeAuthenticatedQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'

const BIDS_PAGE_LIMIT = 100
// Safety cap on how many active bids we'll paginate through for a single
// buyer, so an outlier account can't turn this into an unbounded fan-out of
// requests. Comfortably above what any real premier buyer carries at once.
const MAX_ACTIVE_BIDS = 1000

const fetchAllActiveBids = async (
  accountId: string,
  axiosOptions: ReturnType<typeof buildAxiosRequestConfig>
): Promise<BidListItem[]> => {
  const bidsClient = getBidsClient()
  const bids: BidListItem[] = []
  let offset = 0

  while (bids.length < MAX_ACTIVE_BIDS) {
    const { data } = await bidsClient.getAccountUniqueBids(
      {
        auctionStatus: GetAccountUniqueBidsAuctionStatusEnum.Open,
        bidStatus: [
          GetAccountUniqueBidsBidStatusEnum.Winning,
          GetAccountUniqueBidsBidStatusEnum.Losing,
        ],
        accountId,
        offset,
        limit: BIDS_PAGE_LIMIT,
      },
      axiosOptions
    )
    bids.push(...data.bids)
    offset += BIDS_PAGE_LIMIT

    if (data.bids.length < BIDS_PAGE_LIMIT || bids.length >= data.total) {
      break
    }
  }

  return bids
}

/**
 * Fetches the live-listing IDs the buyer has an active bid on (winning or
 * losing, on an open auction), for the "My Bids" listings filter. Proxy bids
 * count since they're real bids; buy-now purchases are excluded since
 * they're an immediate purchase, not a bid.
 *
 * Deliberately lighter than `uniqueUserBidsQuery`: this only needs listing
 * IDs to feed the search's existing `id` allowlist filter, not the fully
 * orchestrated bid/listing/order/contract data the My Bids page renders.
 */
export const getActiveBidListingIds = async (
  accessToken: string,
  accountId: string
): Promise<string[]> => {
  const axiosOptions = buildAxiosRequestConfig({ accessToken })
  const bids = await fetchAllActiveBids(accountId, axiosOptions)

  const auctionIds = bids
    .filter((bid) => !bid.buyNowBid)
    .map((bid) => bid.auctionId)

  if (auctionIds.length === 0) {
    return []
  }

  const auctionsClient = getAuctionsClient()
  const auctions = await fetchAllResultsById({
    apiClient: auctionsClient,
    query: auctionsClient.getAuctions,
    ids: auctionIds,
    getQueryParameters: (ids, limit) => ({
      auctionId: ids.join(','),
      limit: `${limit}`,
    }),
    responseToResults: (response): AuctionListItems[] =>
      Array.isArray(response) ? response : response.auctions,
    axiosOptions,
  })

  return [...new Set(auctions.map((auction) => auction.listingId))]
}

const activeBidListingIdsQuery = makeAuthenticatedQuery(
  'activeBidListingIds',
  getActiveBidListingIds
)

export default activeBidListingIdsQuery
